import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { RowSelectionState } from '@tanstack/react-table';
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Eye,
  FileCheck,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Send,
  Settings2,
  Sparkles,
  Trash2,
  Undo2,
  UserRound,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  QUICK_SELECT_KEYS,
  QUICK_SELECT_LABELS,
  useSalesOrderPreferencesStore,
} from '../../store/salesOrderPreferencesStore';
import { AutoSaveStatusChip } from '@features/shared/components/AutoSaveStatusChip';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { useSalesOrderStore } from '../../store/salesOrderStore';
import {
  SalesOrderStatus,
  SalesOrderEventType,
  type FirmOfferSummaryDto,
  type SalesOrderDto,
  type SalesOrderLineItemDto,
  type ShippingAddressInput,
  type SubmitSalesOrderRequest,
} from '../../types/salesOrder.types';
import { onSalesOrderEvent } from '../../hooks/useSalesOrderEventStream';
import { FoPickerModal } from '../FoPickerModal';
import { PricingDriftDialog } from '@features/quotations/components/workspace/PricingDriftDialog';
import { priceLevelForGrade } from '../../lib/foPriceLevels';
import {
  SpecAdvisoryBadges,
  computeSpecAdvisories,
  advisoryForLine,
  type SpecAdvisory,
} from '@shared/lineItems';
import type { IntakeSource } from '../../lib/intakeExtraction';
import { FoDetailCard } from '../FoDetailCard';
import { PerGradeBalanceChips } from '../PerGradeBalanceChips';
import { SourceKgNotice } from '../SourceKgNotice';
import { hasKilogramSourceLines, lineWeightKg, PIECE_UNIT } from '../../utils/lineWeight';
import { SalesOrderIntakePanel } from '../SalesOrderIntakePanel';
// Review-pane source viewers reused from the Quotation module. These are
// presentational (no quotation-store coupling); candidates for promotion to
// @shared if more features need them.
import {
  ReviewImageViewer,
  ReviewPdfViewer,
  ReviewSpreadsheetGridViewer,
  ReviewTextViewer,
} from '@features/quotations/components/review';
import { guessPreviewKind } from '@features/quotations/utils/previewKind';
import { SalesOrderLineItemsTable } from '../SalesOrderLineItemsTable';
import { DEFAULT_REBAR_VAT } from '../../utils/pricing';
import { isSpecValidForRematch, REMATCH_INVALID_SPEC_MESSAGE } from '../../utils/lineItem';
import { resolveSalesOrderSizeRule } from '../../utils/sizeRule';
import { isSmallChipDiameter } from '@features/quotations/utils/pricing';
import { SalesOrderFreightBlock } from '../SalesOrderFreightBlock';
import { SalesOrderWeightBreakdown } from '../SalesOrderWeightBreakdown';
import { SalesOrderReviewLineItems } from '../SalesOrderReviewLineItems';
import { FullscreenReviewShell } from '@/components/FullscreenReviewShell';
import {
  SalesOrderReadinessChecklist,
  type ReadinessChecklistHandle,
} from '../readiness/SalesOrderReadinessChecklist';
import { ReadinessBar } from '../readiness/ReadinessBar';
import { PreSubmitReviewDialog } from '../readiness/PreSubmitReviewDialog';
import {
  countBlockers,
  deriveSectionStatuses,
  type SectionKey,
} from '../readiness/sectionStatus';
import { deriveLineItemsReadiness } from '../readiness/lineItemsStatus';
import {
  salesOrderFormSchema,
  draftToFormData,
  formDataToPatch,
  type SalesOrderFormData,
} from '../../schemas/salesOrderFormSchema';
import { SalesOrderPostingStatusCard } from '../SalesOrderPostingStatusCard';
import {
  fetchOutboxStatus,
  optimisticPostingJob,
  outboxKey,
  outboxType,
  postingBlocksSubmit,
  submitBlockedReason,
  type OutboxPostingStatusDto,
} from '@shared/posting';
import { salesOrderService } from '../../services/salesOrderService';
import { SalesOrderReadOnlySummary } from '../SalesOrderReadOnlySummary';
import { SalesOrderStatusBadge } from '../SalesOrderStatusBadge';
import { SalesOrderBulkEditDialog } from '../SalesOrderBulkEditDialog';
import { SalesOrderDetailEmpty } from './SalesOrderDetailEmpty';
import { PanelSkeleton } from '@/components/ui/skeleton';
import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';

interface Props {
  selectedId: number | null;
  canCreate: boolean;
  canSubmit: boolean;
  onCreate: () => void;
  onAfterSave: (serverId: number) => void;
  /** Incremented by the parent each time "New" is clicked — pops the FO picker
   *  modal so creation starts by choosing a Firm Offer. */
  openFoPickerNonce?: number;
}

// Trailing-edge auto-save debounce. Timer lives in the component so cleanup
// happens naturally on unmount or draft switch. Matches QuotationDetailPanel.
const AUTO_SAVE_DEBOUNCE_MS = 2500;

// Relative-age formatter mirrors quotation's formatRelative. Inlined here so
// the SO feature doesn't reach across into quotation utilities.
function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const delta = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (delta < 60) return 'just now';
  const m = Math.floor(delta / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatDateTime12h(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

export function SalesOrderDetailPanel({
  selectedId,
  canCreate,
  canSubmit,
  onCreate,
  onAfterSave,
  openFoPickerNonce,
}: Props) {
  const activeDraft = useSalesOrderStore((s) => s.activeDraft);
  const isLoadingDraft = useSalesOrderStore((s) => s.isLoadingDraft);
  const loadDraftError = useSalesOrderStore((s) => s.loadDraftError);
  const selectedFirmOffer = useSalesOrderStore((s) => s.selectedFirmOffer);
  const pendingFoSummary = useSalesOrderStore((s) => s.pendingFoSummary);
  const eligibilityBanner = useSalesOrderStore((s) => s.eligibilityBanner);
  const isSaving = useSalesOrderStore((s) => s.isSaving);
  const isSubmitting = useSalesOrderStore((s) => s.isSubmitting);
  const pricingDrift = useSalesOrderStore((s) => s.pricingDrift);
  const clearPricingDrift = useSalesOrderStore((s) => s.clearPricingDrift);
  // 86d3c2wd1: needed at submit to resolve a saved-address pick's text, which we send so the
  // backend freezes it into the SO (a posted SO then needs no live address-book re-fetch).
  const customerAddresses = useSalesOrderStore((s) => s.customerAddresses);
  const isLoadingFirmOffer = useSalesOrderStore((s) => s.isLoadingFirmOffer);
  const saveError = useSalesOrderStore((s) => s.saveError);
  const lastSavedAt = useSalesOrderStore((s) => s.lastSavedAt);
  const dirty = useSalesOrderStore((s) => s.dirty);

  const loadDraft = useSalesOrderStore((s) => s.loadDraft);
  const selectFirmOffer = useSalesOrderStore((s) => s.selectFirmOffer);
  const loadLinkedFirmOffer = useSalesOrderStore((s) => s.loadLinkedFirmOffer);
  const updateDraftFields = useSalesOrderStore((s) => s.updateDraftFields);
  const runExtraction = useSalesOrderStore((s) => s.runExtraction);
  const extractingDraftId = useSalesOrderStore((s) => s.extractingDraftId);
  const saveDraft = useSalesOrderStore((s) => s.saveDraft);
  const submitDraft = useSalesOrderStore((s) => s.submitDraft);
  const archiveDraft = useSalesOrderStore((s) => s.archiveDraft);
  const restoreDraft = useSalesOrderStore((s) => s.restoreDraft);
  const deleteDraft = useSalesOrderStore((s) => s.deleteDraft);

  const canArchive = useAuthStore((s) => s.hasPermission(PERMISSIONS.SALES_ORDERS_ARCHIVE_SALES_ORDER));
  const canDelete = useAuthStore((s) => s.hasPermission(PERMISSIONS.SALES_ORDERS_DELETE_SALES_ORDER));

  // ── React Hook Form (86d3afyp6) ──────────────────────────────────────────
  // RHF owns the live edit buffer for the Order Details form so text inputs are
  // uncontrolled — typing no longer re-renders the panel. The store follows via a
  // 200ms debounced watch-sync (keeps dirty/autosave/readiness/submit-gate driven
  // off the store exactly as before). `reset` re-seeds on draft switch (replaces the
  // key={activeDraft.id} remount); FO/company changes re-sync explicitly.
  const formMethods = useForm<SalesOrderFormData>({
    resolver: zodResolver(salesOrderFormSchema),
    mode: 'onBlur',
    defaultValues: activeDraft ? draftToFormData(activeDraft) : undefined,
  });
  const { reset: resetForm, watch: watchForm, getValues: getFormValues } = formMethods;
  const activeDraftId = activeDraft?.id;

  // Flush the form into the store. Guarded by id so a stale flush (after a switch)
  // never writes the previous draft's values onto the new one.
  const flushForm = (expectedId?: number) => {
    const current = useSalesOrderStore.getState().activeDraft;
    if (!current) return;
    if (expectedId != null && current.id !== expectedId) return;
    updateDraftFields(formDataToPatch(getFormValues()));
  };

  // Re-seed on draft switch (id change). Not on the watch-sync (which keeps the id).
  useEffect(() => {
    const draft = useSalesOrderStore.getState().activeDraft;
    if (draft) resetForm(draftToFormData(draft));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDraftId]);

  // 86d3ah33r — the master list splices on SSE, but the open panel binds to activeDraft, which
  // nothing refreshes when the worker finishes. Re-fetch the open draft on a STATUS-CHANGE
  // event for it (Draft -> Submitted). Status-change only, so a normal autosave Updated event
  // (status stays Draft) never triggers a refetch that would clobber RHF edits. 14ykpqtr2kp — the
  // successful-post event carries Status = Submitted (`SalesOrderEventDto.PostingUpdate`); from
  // 86d3mnuxe until then it carried null and this effect never fired, leaving a just-posted SO editable.
  useEffect(() => {
    return onSalesOrderEvent((evt) => {
      if (evt.eventType !== SalesOrderEventType.Updated || evt.status == null) return;
      const draft = useSalesOrderStore.getState().activeDraft;
      if (!draft || draft.id !== evt.salesOrderId || draft.status === evt.status) return;
      void useSalesOrderStore.getState().refreshActiveDraft(evt.salesOrderId);
    });
  }, []);

  // 86d3mnuxe — the outbox posting status for the open SO (the posting lifecycle moved off the entity).
  // Fetched on select + refetched on an SSE posting nudge; drives the read-only gate, the posting/failed
  // card, and the drift dialog. SO is single-row per OrderingKey, so we take the first job.
  const [posting, setPosting] = useState<OutboxPostingStatusDto | null>(null);
  useEffect(() => {
    if (selectedId === null) {
      setPosting(null);
      return;
    }
    let cancelled = false;
    const load = () =>
      fetchOutboxStatus(outboxKey.salesOrder(selectedId))
        .then((jobs) => {
          // 86d3peur0 review — a re-submit enqueues a NEW posting.so job (it does not re-arm the failed
          // one), and jobs are ordered ascending by id, so jobs[0] is the OLDEST. Prefer an in-flight
          // Posting job over a stale Failed sibling; else take the most-recent job.
          if (!cancelled)
            setPosting(jobs.find((j) => j.status === 'Posting') ?? jobs[jobs.length - 1] ?? null);
        })
        .catch(() => {
          if (!cancelled) setPosting(null);
        });
    void load();
    const off = onSalesOrderEvent((evt) => {
      if (evt.salesOrderId === selectedId && evt.postingStatus != null) void load();
    });
    return () => {
      cancelled = true;
      off();
    };
  }, [selectedId]);

  // 86d3mnuxe funnel — resolve a Failed posting intent. Submitter-level (anyone who can submit can
  // cancel their NotReached failure — the flow endpoint authorizes like the submit; the BE refuses a
  // reached cancel). On success the job is Cancelled → excluded from the projection → null is truth.
  const canResolvePosting = useAuthStore(
    (s) =>
      s.hasPermission(PERMISSIONS.SALES_ORDERS_SUBMIT_SALES_ORDER) ||
      s.hasPermission(PERMISSIONS.SYSTEM_MANAGE_OUTBOX),
  );
  const handleCancelPost = useCallback(async () => {
    if (selectedId === null) return;
    try {
      await salesOrderService.cancelPosting(selectedId);
      setPosting(null);
      toast.success('Posting cancelled', {
        description: 'The failed post is closed — you can keep editing and submit again.',
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to cancel the post');
    }
  }, [selectedId]);

  // 86d3mnuxe funnel — Reconcile a REACHED failure: the BE re-arms the same job (frozen decision) so
  // the adopt records the Sales Order NetSuite already accepted. No re-decision, no prompt.
  const handleReconcilePost = useCallback(async () => {
    if (selectedId === null) return;
    try {
      await salesOrderService.reconcilePosting(selectedId);
      setPosting(optimisticPostingJob(outboxKey.salesOrder(selectedId), outboxType.salesOrder));
      toast.info('Reconciling — recovering the record NetSuite already accepted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to start the reconcile');
    }
  }, [selectedId]);

  // 200ms debounced watch-sync → store. Typing stays local to RHF; the store sees the
  // settled value after 200ms quiet. The patch is form-field keys only (formDataToPatch),
  // so lineItems / pricing-snapshot / FO-snapshot are never clobbered.
  useEffect(() => {
    if (activeDraftId == null) return;
    const idAtMount = activeDraftId;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const sub = watchForm(() => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        flushForm(idAtMount);
      }, 200);
    });
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      sub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDraftId]);

  // Load when selectedId changes and we don't already hold that draft.
  useEffect(() => {
    if (selectedId === null) return;
    if (activeDraft?.id === selectedId) return;
    void loadDraft(selectedId);
  }, [selectedId, activeDraft?.id, loadDraft]);

  // Review mode — toggles the body between compose view (table + intake) and
  // a side-by-side review pane showing the extraction source vs the matched
  // lines. Mirrors QuotationDetailPanel's Review button (line 932-944).
  const [reviewMode, setReviewMode] = useState(false);

  // Uploaded files for the current draft, held in memory only (never persisted).
  // Powers the in-session file preview in the Review pane; the DB keeps just the
  // filename. Reset on a genuine draft switch (not on a first-save id promotion).
  const [sessionFiles, setSessionFiles] = useState<File[]>([]);

  // Re-extract dialog — opens the intake (paste/upload) inline panel inside
  // a modal so users can extract more lines without leaving the populated
  // table. The empty-state path renders the same panel inline (no modal).
  const [extractDialogOpen, setExtractDialogOpen] = useState(false);

  // Line-item view-mode tab. Mirrors quotation's All / Spec / Pricing
  // toggle — switches which columns are visible (not which rows). All shows
  // both spec + pricing columns; Spec hides pricing; Pricing hides spec.
  type LineItemsViewMode = 'all' | 'spec' | 'pricing';
  // 86d3cxrjc: default to All view (spec + pricing columns) — was 'spec'.
  const [lineFilter, setLineFilter] = useState<LineItemsViewMode>('all');

  // Row selection lifted here so the bulk-action subheader can act on the
  // same selection the table renders. Keyed by line item clientId
  // (the table's getRowId).
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Prune selection keys pointing to rows that no longer exist (delete /
  // re-extract). Mirrors quotation's housekeeping at QuotationLineItemsTable
  // line ~280.
  useEffect(() => {
    if (!activeDraft) return;
    const validIds = new Set(activeDraft.lineItems.map((li) => li.clientId));
    setRowSelection((prev) => {
      let changed = false;
      const next: RowSelectionState = {};
      for (const [id, v] of Object.entries(prev)) {
        if (validIds.has(id)) next[id] = v;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [activeDraft]);

  const selectedClientIds = useMemo(
    () => Object.entries(rowSelection).filter(([, v]) => v).map(([id]) => id),
    [rowSelection],
  );
  const selectedCount = selectedClientIds.length;

  // ── Order Details cabinet ────────────────────────────────────────────────
  // Single trigger bar under the header opens an absolute overlay containing
  // the FO picker + inherited FO summary + the editable order form, organised
  // into labelled sections. Auto-opens when a draft has no FO yet so the user
  // can pick one immediately. Closes on outside click and draft switch.
  const [cabinetOpen, setCabinetOpen] = useState(false);
  const cabinetTriggerRef = useRef<HTMLButtonElement | null>(null);
  const cabinetRef = useRef<HTMLDivElement | null>(null);

  // FO picker modal — the creation seed. Pops automatically when the parent
  // signals a new draft (nonce bumps on "New"), and via the "Change FO" /
  // "Select Firm Offer" button for an existing/blank draft.
  const [foModalOpen, setFoModalOpen] = useState(false);
  useEffect(() => {
    if (openFoPickerNonce && openFoPickerNonce > 0) setFoModalOpen(true);
  }, [openFoPickerNonce]);

  // True from FO pick until the detail loads + the first save completes — drives
  // the "drafting your sales order…" message in the main panel.

  // Readiness Checklist plumbing (ticket 86d385wc0).
  const checklistRef = useRef<ReadinessChecklistHandle>(null);
  const [preSubmitOpen, setPreSubmitOpen] = useState(false);


  useEffect(() => {
    if (!cabinetOpen) return;
    const handler = (e: PointerEvent) => {
      // The FO picker modal renders in a portal (outside cabinetRef). Clicking
      // inside it must NOT count as an outside-click that closes the cabinet.
      if (foModalOpen) return;
      // A Radix Select/Popover/Dropdown/Dialog is open → this click is dismissing
      // THAT overlay, not the cabinet. Its content is portaled outside cabinetRef
      // so target-containment is unreliable. Listening in capture ensures we see
      // the overlay before Radix may unmount it while the event bubbles.
      if (
        document.querySelector(
          '[data-radix-popper-content-wrapper],[role="listbox"][data-state="open"],[role="menu"][data-state="open"],[role="dialog"][data-state="open"]',
        )
      ) {
        return;
      }
      const target = e.target as Node;
      if (cabinetRef.current?.contains(target)) return;
      if (cabinetTriggerRef.current?.contains(target)) return;
      // The sticky readiness bar lives outside cabinetRef but is part of the
      // cabinet's control surface — its jump chips open/scroll sections. Clicking
      // one must NOT close (then re-open) the cabinet.
      const el = target instanceof Element ? target : target.parentElement;
      if (el?.closest('[data-readiness-bar]')) return;
      setCabinetOpen(false);
    };
    window.addEventListener('pointerdown', handler, true);
    return () => window.removeEventListener('pointerdown', handler, true);
  }, [cabinetOpen, foModalOpen]);

  // Close the cabinet when switching to a different draft. The first save of a
  // new draft promotes selectedId (null → serverId) for the SAME draft on screen
  // — that must NOT collapse a cabinet the user opened.
  const prevSelectedIdRef = useRef(selectedId);
  useEffect(() => {
    const prev = prevSelectedIdRef.current;
    prevSelectedIdRef.current = selectedId;
    if (prev === null && selectedId != null && selectedId === activeDraft?.id) return;
    setCabinetOpen(false);
    // Genuine draft switch (not a first-save id promotion): drop the previous
    // draft's in-session files and exit any open review.
    setSessionFiles([]);
    setReviewMode(false);
  }, [selectedId]);

  // ── Auto-save effect ─────────────────────────────────────────────────────
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    if (!activeDraft) return;
    if (activeDraft.status !== SalesOrderStatus.Draft) return;
    if (activeDraft.isArchived) return; // 86d3d2hvz: archived = terminal, never auto-save
    if (isLoadingFirmOffer) return;
    if (isSaving) return;
    if (!dirty) return;

    autoSaveTimerRef.current = setTimeout(() => {
      void runAutoSave();
    }, AUTO_SAVE_DEBOUNCE_MS);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, activeDraft?.id, activeDraft?.status, activeDraft?.isArchived, isLoadingFirmOffer, isSaving]);

  async function runAutoSave() {
    flushForm(activeDraftId); // commit any RHF edits still inside the 200ms debounce
    const saved = await saveDraft();
    if (saved) onAfterSave(saved.id);
  }

  // ── Readiness model (single source of truth for the bar + submit gate) ────
  // Same pure derivation the checklist cards use, so badges, bar and gate agree.
  const sections = useMemo(
    () =>
      activeDraft
        ? deriveSectionStatuses(activeDraft, Boolean(activeDraft.linkedFirmOfferInternalId))
        : [],
    [activeDraft],
  );
  const lineItemsReadiness = useMemo(
    () => deriveLineItemsReadiness(activeDraft?.lineItems ?? [], selectedFirmOffer?.balanceKg),
    // Keyed on lineItems (stable ref across non-line edits) so typing in a text field
    // doesn't re-derive readiness every keystroke (86d3afaup). The FO balance feeds the
    // total-tonnage blocker (14ykpqtv3qx).
    [activeDraft?.lineItems, selectedFirmOffer?.balanceKg],
  );
  // 14ykpqtv3qw — the source listed its quantities in kg; they're kept as pieces, with a warning.
  const sourceListedKg = useMemo(
    () => hasKilogramSourceLines(activeDraft?.lineItems ?? []),
    [activeDraft?.lineItems],
  );

  // 86d3c2wd1: resolve a saved address-book id to its text (single line) from the loaded book,
  // sent at submit so the backend freezes it into the SO denorm columns + snapshot.
  function resolveSavedAddrText(addressId: string | null): string | undefined {
    if (!addressId) return undefined;
    const found = customerAddresses.find((a) => a.internalId === addressId);
    const text = found?.addrText?.trim() || found?.addressee?.trim() || '';
    return text ? text.replace(/\s*\n\s*/g, ', ') : undefined;
  }

  // Build the submit-time ShippingAddressInput from the (saved) draft's flat fields —
  // RHF owns these now (86d3afyp6), so we read them off the saved draft, not a memo.
  function draftToShippingInput(d: SalesOrderDto): ShippingAddressInput {
    if (d.shippingAddressId)
      return {
        savedAddressId: d.shippingAddressId,
        savedAddressText: resolveSavedAddrText(d.shippingAddressId),
        custom: null,
      };
    if (d.shippingAddresseeName || d.shippingAddrLine1) {
      return {
        savedAddressId: null,
        custom: {
          addressee: d.shippingAddresseeName ?? '',
          addrLine1: d.shippingAddrLine1 ?? '',
          addrLine2: d.shippingAddrLine2,
          addrLine3: d.shippingAddrLine3,
          cityTownId: d.shippingCityTownId,
          stateProvinceId: d.shippingStateProvinceId,
          zip: d.shippingZip,
          countryId: d.shippingCountryId ?? 'PH',
          saveToCustomer: false,
        },
      };
    }
    return { savedAddressId: null, custom: null };
  }

  async function handleFoPick(fo: FirmOfferSummaryDto) {
    if (activeDraft && activeDraft.lineItems.length > 0) {
      if (!confirm('Changing FO will clear extracted line items. Continue?')) return;
      updateDraftFields({ lineItems: [] });
    }
    // selectFirmOffer flips isLoadingFirmOffer, which drives the workspace-shell loading
    // skeletons (86d3a1q88 — unified with the reload path). Do the first save once the
    // detail lands (assigns the server id). On failure selectFirmOffer surfaces the
    // inline eligibility error and we don't save.
    const loaded = await selectFirmOffer(fo);
    if (loaded) {
      // FO seeds many form fields in the store (customerPoNumber, termsId, waivedFreight…)
      // without changing draft.id — re-sync the form so the watch-sync can't clobber them
      // back (86d3afyp6). Must happen BEFORE runAutoSave's flush.
      const d = useSalesOrderStore.getState().activeDraft;
      if (d) resetForm(draftToFormData(d));
      await runAutoSave();
    }
  }

  async function handleManualSave() {
    await runAutoSave();
  }

  // Clear chip — wipes the current row selection. Does NOT delete line items.
  function handleClearSelection() {
    if (selectedCount === 0) return;
    setRowSelection({});
  }

  function handleBulkDelete() {
    if (!activeDraft || selectedCount === 0) return;
    if (!confirm(`Delete ${selectedCount} selected line item${selectedCount === 1 ? '' : 's'}?`)) return;
    const remove = new Set(selectedClientIds);
    updateDraftFields({
      lineItems: activeDraft.lineItems.filter((li) => !remove.has(li.clientId)),
    });
    setRowSelection({});
  }

  // ── Re-match (per-row + bulk) ───────────────────────────────────────────
  // Hits POST /api/intake/re-match with the line's current spec; replaces the
  // line's matched fields (matchedItemId, matchedSalesDescription, weight,
  // base/avg price, match status) with whatever the matcher returns. Spec
  // fields are kept as-is — the user's intent is "find a better match for
  // THIS spec", not "re-extract".
  interface RematchedRaw {
    rawDescription?: string | null;
    diameterMm: number | null;
    lengthM: number | null;
    gradeCode: number | null;
    isWeldable: boolean;
    quantity: number | null;
    unit: string;
    notes: string | null;
    matchStatus: string | null;
    matchedItemId: string | null;
    matchedSalesDescription: string | null;
    totalWeightKg: number | null;
    weightPerPcKg: number | null;
    basePricePerPc: number | null;
    averagePricePerPc: number | null;
    promotedFromGrade: number | null;
  }

  // Returns true on a successful re-match. Errors are caught + toasted here (never
  // thrown), so callers branch on the boolean — see handleBulkRematch's count (86d3cnzeu).
  async function rematchSingle(clientId: string): Promise<boolean> {
    // Read the LIVE draft, not the render-time closure `activeDraft`: auto-rematch fires
    // immediately after a spec edit (before a re-render), so the closure wouldn't carry the
    // just-edited spec yet. Reading getState() guarantees we re-match on the new spec (86d3cnzhr).
    const current = useSalesOrderStore.getState().activeDraft;
    if (!current) return false;
    const line = current.lineItems.find((li) => li.clientId === clientId);
    if (!line) return false;
    // 86d3dxnd7: pre-flight gate on the SAME rules the server validator enforces
    // (valid grade + positive diameter/length). Guides BEFORE the call so an
    // invalid spec never reaches the endpoint and 400s.
    if (!isSpecValidForRematch(line)) {
      toast.error(REMATCH_INVALID_SPEC_MESSAGE);
      return false;
    }
    try {
      // Local matcher (rebar masterdata cache — no NetSuite round-trip).
      const { data } = await apiClient.post<ApiResponse<RematchedRaw>>(
        '/intake/re-match',
        {
          rawDescription: line.rawDescription,
          diameterMm: line.diameterMm,
          lengthM: line.lengthM,
          gradeCode: Number(line.gradeCode),
          isWeldable: line.isWeldable,
          quantity: line.quantity,
          // 14ykpqtv3qw — always pieces: a `kgs` unit makes the matcher return weight = quantity.
          unit: PIECE_UNIT,
          notes: line.notes,
        },
      );
      if (!data.success || !data.data) {
        throw new Error(data.message || 'Re-match failed');
      }
      const r = data.data;
      // Re-match finds a better SKU; it must NOT touch the line's price level — that's
      // the coordinator's pick (or the FO default already stamped). Leaving it out of
      // the patch preserves it and avoids a recompute flicker. (Revert resets it.)
      const patched: Partial<SalesOrderLineItemDto> = {
        matchStatus: r.matchStatus,
        matchedItemId: r.matchedItemId,
        matchedSalesDescription: r.matchedSalesDescription,
        weightKg: lineWeightKg({ quantity: line.quantity, weightPerPcKg: r.weightPerPcKg, weightKg: r.totalWeightKg }),
        weightPerPcKg: r.weightPerPcKg,
        basePricePerPc: r.basePricePerPc,
        averagePricePerPc: r.averagePricePerPc,
        promotedFromGrade: r.promotedFromGrade,
        isEdited: true,
      };
      // Write against the LIVE store state, not the render-time closure `activeDraft`.
      // A bulk re-match loops this with `await` between rows; mapping the closure array
      // each time started from the same stale snapshot → every write clobbered the prior
      // (only the last row survived). Reading getState() here makes the writes accumulate
      // (86d3cnzeu). `updateDraftFields` itself already merges into current state.
      const latest = useSalesOrderStore.getState().activeDraft;
      if (!latest) return false;
      updateDraftFields({
        lineItems: latest.lineItems.map((li) =>
          li.clientId === clientId ? { ...li, ...patched } : li,
        ),
      });
      return true;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Re-match failed');
      return false;
    }
  }

  async function handleBulkRematch() {
    if (!activeDraft || selectedCount === 0) return;
    let succeeded = 0;
    for (const id of selectedClientIds) {
      if (await rematchSingle(id)) succeeded++;
    }
    if (succeeded > 0) {
      toast.success(
        `Re-matched ${succeeded} of ${selectedCount} line${selectedCount === 1 ? '' : 's'}`,
      );
    }
    setRowSelection({});
  }

  // 86d3cw2ub: shared auto-re-match for the non-cell spec mutations (bulk edit +
  // single/bulk revert). The per-cell edit path already re-matches via commitSpec
  // (#377); these handlers mutate the same match-driving fields (diameter / length /
  // grade / weldable) but used to leave the matched item stale, so a line's grade
  // could diverge from its item and the submit guard would reject it. Mirrors
  // commitSpec: only lines whose spec is now COMPLETE are re-matched — rematchSingle
  // toasts an error on an incomplete spec, so we MUST filter here, before calling it.
  // Reads the LIVE draft via getState() so it sees the just-applied patch
  // (updateDraftFields is a synchronous Zustand set). Fire-and-forget like commitSpec;
  // rematchSingle is silent on success and toasts only on failure.
  async function rematchAfterSpecChange(clientIds: string[]) {
    const current = useSalesOrderStore.getState().activeDraft;
    if (!current) return;
    const ready = clientIds.filter((id) => {
      const li = current.lineItems.find((l) => l.clientId === id);
      return li != null && isSpecValidForRematch(li);
    });
    for (const id of ready) {
      await rematchSingle(id);
    }
  }

  // ── Revert (per-row + bulk) ────────────────────────────────────────────
  // Uses originalSpecJson stamped at extraction time. Restores diameter, length,
  // grade, weldable, quantity, unit + resets the price level to the FO default.
  // revertOne itself doesn't touch matched fields; the revert HANDLERS call
  // rematchAfterSpecChange afterward so the matched item tracks the restored spec
  // (save never re-runs the matcher — 86d3cw2ub).
  interface OriginalSpec {
    diameterMm: number | null;
    lengthM: number | null;
    gradeCode: string | null;
    isWeldable: boolean;
    quantity: number | null;
    unit: string;
  }

  function revertOne(line: SalesOrderLineItemDto): SalesOrderLineItemDto | null {
    if (!line.originalSpecJson) return null;
    try {
      const o = JSON.parse(line.originalSpecJson) as OriginalSpec;
      return {
        ...line,
        diameterMm: o.diameterMm,
        lengthM: o.lengthM,
        gradeCode: o.gradeCode,
        isWeldable: o.isWeldable,
        quantity: o.quantity,
        // 14ykpqtv3qw — revert restores the spec, never the source's kg unit: SO lines are pieces.
        unit: PIECE_UNIT,
        weightKg: lineWeightKg({ ...line, quantity: o.quantity }),
        // Reset the price level back to the FO default for the reverted grade — revert
        // restores the as-extracted state, including the price level the coordinator
        // may have changed. Null when the FO has no priced line for that grade.
        priceLevel: priceLevelForGrade(o.gradeCode, selectedFirmOffer?.perGradePriceLevel),
        isEdited: false,
      };
    } catch {
      return null;
    }
  }

  function handleRevertSingle(clientId: string) {
    if (!activeDraft) return;
    const line = activeDraft.lineItems.find((li) => li.clientId === clientId);
    if (!line) return;
    const reverted = revertOne(line);
    if (!reverted) {
      toast.error('No original spec recorded for this line.');
      return;
    }
    updateDraftFields({
      lineItems: activeDraft.lineItems.map((li) =>
        li.clientId === clientId ? reverted : li,
      ),
    });
    // 86d3cw2ub: re-match the reverted line so its matched item tracks the restored spec.
    void rematchAfterSpecChange([clientId]);
  }

  function handleBulkRevert() {
    if (!activeDraft || selectedCount === 0) return;
    const sel = new Set(selectedClientIds);
    const revertedIds: string[] = [];
    const next = activeDraft.lineItems.map((li) => {
      if (!sel.has(li.clientId)) return li;
      const r = revertOne(li);
      if (!r) return li;
      revertedIds.push(li.clientId);
      return r;
    });
    if (revertedIds.length === 0) {
      toast.error('None of the selected rows have a recorded original spec.');
      return;
    }
    updateDraftFields({ lineItems: next });
    toast.success(
      `Reverted ${revertedIds.length} of ${selectedCount} line${selectedCount === 1 ? '' : 's'}`,
    );
    // 86d3cw2ub: re-match ONLY the rows actually reverted (their restored spec may
    // differ from the now-stale match); the helper filters to spec-complete lines.
    void rematchAfterSpecChange(revertedIds);
  }

  // ── Bulk edit ──────────────────────────────────────────────────────────
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  // ── Quick Select derived ID lists ──────────────────────────────────────
  // Categorises every line by the buckets the Quick Select buttons toggle.
  // Computed once per draft change so each button can resolve its match
  // group + count in O(1).
  const quickSelect = useMemo(() => {
    const small: string[] = [];
    const big: string[] = [];
    const noLength: string[] = [];
    const noGrade: string[] = [];
    const noQuantity: string[] = [];
    const noMatch: string[] = [];
    const weldable: string[] = [];
    const notWeldable: string[] = [];
    const items = activeDraft?.lineItems ?? [];
    for (const li of items) {
      // 14ykpqtr22u — split at BIG_DIAMETER_MIN_MM (16mm is big): every sized row lands in one chip.
      if (li.diameterMm != null) (isSmallChipDiameter(li.diameterMm) ? small : big).push(li.clientId);
      if (li.lengthM == null) noLength.push(li.clientId);
      if (li.gradeCode == null) noGrade.push(li.clientId);
      if (li.quantity == null || li.quantity === 0) noQuantity.push(li.clientId);
      if (li.matchStatus === 'NO_MATCH') noMatch.push(li.clientId);
      if (li.isWeldable === true) weldable.push(li.clientId);
      if (li.isWeldable === false) notWeldable.push(li.clientId);
    }
    return {
      smallDiameter: small,
      bigDiameter: big,
      noLength,
      noGrade,
      noQuantity,
      noMatch,
      weldable,
      notWeldable,
    };
    // Keyed on lineItems only — quick-select buckets don't depend on header fields (86d3afaup).
  }, [activeDraft?.lineItems]);

  // Spec-rule advisories (86d3hk6pg): flag lines that violate a sales spec rule
  // (≥16mm→G60, G60→weldable) so the coordinator can apply the fix optionally — the
  // extraction ingests the client's actual request untouched.
  const specAdvisories = useMemo(
    () => computeSpecAdvisories(activeDraft?.lineItems ?? [], (li) => li.clientId),
    [activeDraft?.lineItems],
  );

  const visibleQuickSelects = useSalesOrderPreferencesStore(
    (s) => s.visibleQuickSelects,
  );
  const setQuickSelectVisible = useSalesOrderPreferencesStore(
    (s) => s.setQuickSelectVisible,
  );

  // Toggle: if every id in the group is already selected, unselect them;
  // otherwise add them all to selection. Mirrors quotation's behaviour.
  function toggleGroupSelection(ids: string[]) {
    if (ids.length === 0) return;
    const allSelected = ids.every((id) => rowSelection[id]);
    if (allSelected) {
      const next = { ...rowSelection };
      ids.forEach((id) => {
        delete next[id];
      });
      setRowSelection(next);
    } else {
      const next = { ...rowSelection };
      ids.forEach((id) => (next[id] = true));
      setRowSelection(next);
    }
  }

  function handleBulkEditApply(patch: Partial<SalesOrderLineItemDto>) {
    if (!activeDraft || selectedCount === 0) return;
    const sel = new Set(selectedClientIds);
    updateDraftFields({
      lineItems: activeDraft.lineItems.map((li) =>
        sel.has(li.clientId) ? { ...li, ...patch, isEdited: true } : li,
      ),
    });
    toast.success(`Updated ${selectedCount} line${selectedCount === 1 ? '' : 's'}`);
    // 86d3cw2ub: if the bulk patch touched a match-driving field, re-match the affected
    // lines so their matched item tracks the new spec (mirrors commitSpec). qty/unit-only
    // patches don't change the match — skip. Patch-level check is enough (re-match is
    // idempotent); no per-line diff needed.
    const touchedMatchField = (
      ['diameterMm', 'lengthM', 'gradeCode', 'isWeldable'] as const
    ).some((k) => k in patch);
    if (touchedMatchField) {
      void rematchAfterSpecChange(selectedClientIds);
    }
  }

  // 86d3hk6pg: apply a spec advisory's recommended fix to all its affected lines at once.
  // Patches spec fields (+ price level when the grade changes, mirroring revertOne), marks
  // them edited, re-matches, and offers Undo (restore the pre-apply snapshot + re-match).
  function applyAdvisory(advisory: SpecAdvisory) {
    if (!activeDraft) return;
    const ids = new Set(advisory.ids);
    const snapshot = activeDraft.lineItems.filter((li) => ids.has(li.clientId)).map((li) => ({ ...li }));
    if (snapshot.length === 0) return;
    const touchesGrade = 'gradeCode' in advisory.patch;

    updateDraftFields({
      lineItems: activeDraft.lineItems.map((li) => {
        if (!ids.has(li.clientId)) return li;
        const next = { ...li, ...advisory.patch, isEdited: true };
        if (touchesGrade) {
          next.priceLevel = priceLevelForGrade(next.gradeCode, selectedFirmOffer?.perGradePriceLevel);
        }
        return next;
      }),
    });
    void rematchAfterSpecChange(advisory.ids);

    toastAdvisoryApplied(advisory, snapshot);
  }

  // Apply just ONE row's advisory (from its Status-cell chip). Reuses applyAdvisory with a
  // single-line advisory built from the row's active rule.
  function applyRowAdvisory(clientId: string) {
    const line = activeDraft?.lineItems.find((li) => li.clientId === clientId);
    if (!line) return;
    const rule = advisoryForLine(line);
    if (!rule) return;
    applyAdvisory({ ...rule, count: 1, ids: [clientId] });
  }

  function toastAdvisoryApplied(advisory: SpecAdvisory, snapshot: SalesOrderLineItemDto[]) {
    toast.success(
      `${advisory.label} — applied to ${snapshot.length} line${snapshot.length === 1 ? '' : 's'}`,
      {
        action: {
          label: 'Undo',
          onClick: () => {
            const cur = useSalesOrderStore.getState().activeDraft;
            if (!cur) return;
            const byId = new Map(snapshot.map((s) => [s.clientId, s]));
            updateDraftFields({
              lineItems: cur.lineItems.map((li) => byId.get(li.clientId) ?? li),
            });
            void rematchAfterSpecChange(advisory.ids);
          },
        },
      },
    );
  }

  // Re-extract REPLACES the draft's lines + source wholesale (mirrors quotation;
  // the replace-confirm is handled in confirmReplaceIfNeeded before extraction).
  // Captures the source so the Review pane lights up: text persists; filenames
  // persist (capped to the 256-char column); the File bytes live only in session
  // memory for an in-session preview.
  // Kick off a background extraction (the store owns the fetch + apply + per-FO
  // price-level stamping, scoped to this draft id). Preview the new files
  // immediately; callers that opened the modal close it right after so it doesn't
  // block on the AI run (86d3a2agk).
  function handleStartExtraction(source: IntakeSource) {
    if (!activeDraft) return;
    setSessionFiles(source.files);
    void runExtraction(source);
  }

  // Mirrors quotation's warn-before-replace: re-extracting discards the current
  // lines (and any manual edits). Returns true to proceed.
  function confirmReplaceIfNeeded(): boolean {
    if (!activeDraft || activeDraft.lineItems.length === 0) return true;
    return window.confirm(
      `Re-extracting will REPLACE the current ${activeDraft.lineItems.length} line item(s) on this draft. Continue?`,
    );
  }

  function handleAddLine() {
    if (!activeDraft) return;
    const newLine = {
      id: 0,
      clientId:
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      sortOrder: activeDraft.lineItems.length + 1,
      diameterMm: null,
      lengthM: null,
      gradeCode: null,
      isWeldable: false,
      quantity: null,
      unit: 'pcs',
      weightKg: null,
      weightPerPcKg: null,
      matchStatus: 'manual',
      rawDescription: null,
      notes: null,
      matchedSalesDescription: null,
      matchedItemId: null,
      priceLevel: null,
      basePricePerPc: null,
      averagePricePerPc: null,
      originalSpecJson: null,
      isEdited: true,
      promotedFromGrade: null,
    };
    updateDraftFields({ lineItems: [...activeDraft.lineItems, newLine] });
  }

  async function handleArchive() {
    if (!activeDraft) return;
    if (!confirm(`Archive ${activeDraft.salesOrderNumber || 'this draft'}?`)) return;
    await archiveDraft(activeDraft.id);
  }

  async function handleRestore() {
    if (!activeDraft) return;
    await restoreDraft(activeDraft.id);
  }

  async function handleDelete() {
    if (!activeDraft) return;
    if (!confirm(`Delete ${activeDraft.salesOrderNumber || 'this draft'}?`)) return;
    await deleteDraft(activeDraft.id);
  }

  // Open a section in the checklist (opening the cabinet first if needed) and
  // scroll to it — the readiness bar's jump targets.
  function jumpToSection(key: SectionKey) {
    setCabinetOpen(true);
    requestAnimationFrame(() => checklistRef.current?.openSection(key));
  }

  // Line items live in the main body (not the cabinet) — close the cabinet to
  // reveal the table and scroll to it.
  function jumpToLineItems() {
    setCabinetOpen(false);
    requestAnimationFrame(() => {
      document.getElementById('so-line-items')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // Submit click — never hard-disabled for blockers (per ticket). When not
  // ready, surface the count and route to the first blocker; else open the
  // pre-submit review (the final recognition checkpoint).
  function handleSubmitClick() {
    if (!activeDraft || !activeDraft.linkedFirmOfferInternalId) {
      toast.error('Pick a Firm Offer first');
      return;
    }
    const blockedSection = sections.find((s) => s.blockers.length > 0);
    const lineBlocked = Boolean(lineItemsReadiness.blocker);
    if (blockedSection || lineBlocked) {
      const n = countBlockers(sections) + (lineBlocked ? 1 : 0);
      toast.error(`${n} ${n === 1 ? 'thing needs' : 'things need'} attention before submitting`);
      if (blockedSection) jumpToSection(blockedSection.key);
      else jumpToLineItems();
      return;
    }
    setPreSubmitOpen(true);
  }

  async function confirmSubmit() {
    if (!activeDraft || !activeDraft.linkedFirmOfferInternalId) {
      toast.error('Pick a Firm Offer first');
      return;
    }
    // 86d3mnuxe funnel — reached failures never route here any more: Submit is disabled while a
    // Failed job exists, and the card's Reconcile re-arms the frozen job server-side instead of
    // re-claiming. This path is always a genuine (re-)submission of what's on screen.
    flushForm(activeDraftId); // commit any RHF edits still inside the 200ms debounce
    const saved = await saveDraft();
    if (!saved) return;
    onAfterSave(saved.id);

    const request: SubmitSalesOrderRequest = {
      salesOrderId: saved.id,
      linkedFirmOfferInternalId: saved.linkedFirmOfferInternalId!,
      items: saved.lineItems.map((li) => ({
        matchedItemId: li.matchedItemId ?? '',
        quantity: li.quantity ?? 0,
        itemCode: li.matchedItemId,
        gradeCode: li.gradeCode,
        priceLevel: li.priceLevel,
      })),
      customerPoNumber: saved.customerPoNumber,
      poExpiryDate: saved.poExpiryDate,
      deliveryDueDate: saved.deliveryDueDate,
      stateProvinceId: saved.shippingStateProvinceId ?? '',
      cityTownId: saved.shippingCityTownId ?? '',
      waivedFreight: saved.waivedFreight,
      separateBilling: saved.separateBilling,
      includeFreightLine: saved.includeFreightLine,
      shippingAddress: draftToShippingInput(saved),
      doCustCompanyId: saved.doCustCompanyId,
      doCustCompanyName: saved.doCustCompanyName,
      billingAddress: saved.billingAddressId
        ? {
            savedAddressId: saved.billingAddressId,
            savedAddressText: resolveSavedAddrText(saved.billingAddressId),
            custom: null,
          }
        : saved.billingAddrLine1 || saved.billingAddresseeName
          ? {
              savedAddressId: null,
              custom: {
                addressee: saved.billingAddresseeName ?? '',
                addrLine1: saved.billingAddrLine1 ?? '',
                addrLine2: saved.billingAddrLine2,
                addrLine3: saved.billingAddrLine3,
                cityTownId: saved.billingCityTownId,
                stateProvinceId: saved.billingStateProvinceId,
                zip: saved.billingZip,
                countryId: saved.billingCountryId ?? 'PH',
                saveToCustomer: false,
              },
            }
          : null,
      termsId: saved.termsId,
      termsName: saved.termsName,
      projectName: saved.projectName,
      alertInvoiceECopy: saved.alertInvoiceECopy,
      alertManualComputation: saved.alertManualComputation,
      alertMc2Copies: saved.alertMc2Copies,
      alertDrPicture: saved.alertDrPicture,
      alertDrAttachment: saved.alertDrAttachment,
      alertBrnTraceability: saved.alertBrnTraceability,
      scheduleId: saved.scheduleId,
      authorizedReceivers: saved.authorizedReceivers,
      otherInstructions: saved.otherInstructions,
      soRemarks: saved.soRemarks,
      subPoOrderlistLink: saved.subPoOrderlistLink,
      invoiceToLetter: saved.invoiceToLetter,
      officeContact1: saved.officeContact1,
      contactNumber1: saved.contactNumber1,
      officeContact2: saved.officeContact2,
      contactNumber2: saved.contactNumber2,
      officeContact3: saved.officeContact3,
      contactNumber3: saved.contactNumber3,
      isTrailer: saved.isTrailer,
      isMf: saved.isMf,
      isPpeRequired: saved.isPpeRequired,
      isPermitRequired: saved.isPermitRequired,
      isPriority: saved.isPriority,
      isSpotOrder: saved.isSpotOrder,
      isReprint: saved.isReprint,
      waitingForOfficialPo: saved.waitingForOfficialPo,
      freightChargePerTon: saved.freightChargePerTon, // 86d3fteqa: editable rate drives the charge
      actualFreightPerMt: saved.actualFreightPerMt, // informational only
      isDiscountedFreight: saved.isDiscountedFreight,
      discountDetails: saved.discountDetails,
      reasonForClosing: saved.reasonForClosing,
      subagentId: saved.subagentId,
      date: saved.date,
    };
    const claimed = await submitDraft(request);
    // 86d3peur0 #12 — engage the read-only gate + posting card NOW. The claim has committed the job, but
    // `posting` is only refetched on the SSE nudge, so without this the panel stays editable with Submit
    // live for a whole round-trip after the click. The refetch replaces this echo with the server's row.
    // Gated on the return value: submitDraft SWALLOWS failures (toasts + returns false), so an unconditional
    // flip here would show "posting…" for a submit that never started.
    if (claimed) {
      setPosting(optimisticPostingJob(outboxKey.salesOrder(request.salesOrderId), outboxType.salesOrder));
    }
    setPreSubmitOpen(false);
  }

  // Drift refresh: re-read the FO, re-stamp the draft's per-grade markup snapshot from
  // the now-current values, then re-save + re-submit. Mirrors the quotation Refresh flow.
  async function handleRefreshAndResubmit() {
    clearPricingDrift();
    await loadLinkedFirmOffer();
    const fo = useSalesOrderStore.getState().selectedFirmOffer;
    if (fo) {
      updateDraftFields({
        grade230Markup: fo.pricing.perGrade.grade230.markupPct,
        grade275Markup: fo.pricing.perGrade.grade280.markupPct,
        grade415Markup: fo.pricing.perGrade.grade420.markupPct,
        grade520Markup: fo.pricing.perGrade.grade520.markupPct,
        grade550Markup: fo.pricing.perGrade.grade550.markupPct,
      });
    }
    await confirmSubmit();
  }

  // 86d3mnuxe funnel — NotReached Retry: cancel the failed intent, then run the normal (re-)submit
  // of what's on screen. A genuine re-decision — the "Pricing changed during submission" dialog can
  // fire on the way (the store surfaces the sync 409). Replaces the old failureDetailJson-parsing
  // "Refresh & resubmit": post-FIX 5, drift can never dead-letter a job, so failed jobs carry no
  // drift payload.
  async function handleRetryFailedPost() {
    if (selectedId === null) return;
    try {
      await salesOrderService.cancelPosting(selectedId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to retry the post');
      return;
    }
    await confirmSubmit();
  }

  // ── Render gates ─────────────────────────────────────────────────────────

  if (!selectedId && !activeDraft) {
    return <SalesOrderDetailEmpty canCreate={canCreate} onCreate={onCreate} />;
  }

  // Mask the whole panel while a draft is loading OR while the loaded draft doesn't
  // yet match the current selection (a switch in flight) — never render another
  // draft's data before the system confirms it belongs to the selected draft
  // (86d3abjqx). An id promotion (a new draft's null→serverId) keeps
  // activeDraft.id === selectedId, so it doesn't trip this.
  if (
    isLoadingDraft ||
    (selectedId !== null && activeDraft !== null && activeDraft.id !== selectedId)
  ) {
    return (
      <div className="p-4">
        <PanelSkeleton rows={6} />
      </div>
    );
  }

  if (loadDraftError && !activeDraft) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-destructive">
          {loadDraftError}
        </div>
      </div>
    );
  }

  if (!activeDraft) return null;

  const isSubmitted = activeDraft.status === SalesOrderStatus.Submitted;
  // 86d3mnuxe async posting states from the OUTBOX. Posting = in flight (not editable); Failed keeps the
  // Draft entity editable so the coordinator can fix and retry / refresh-and-resubmit.
  // 86d3peur0 review — once the SO is actually Submitted, suppress any lingering Failed job (a
  // failed-then-succeeded re-post leaves the old Failed job in the projection for the retention window;
  // its successful successor is Done and drops off, so it would otherwise show a stuck "posting failed").
  const isPosting = !isSubmitted && posting?.status === 'Posting';
  const isPostingFailed = !isSubmitted && posting?.status === 'Failed';
  const isReadOnly = isSubmitted || isPosting;
  // 14ykpqtr22u — a posted SO keeps the size rule it was posted under (its weight split doesn't move
  // when the rule changes). A plain value, not a hook: this runs after the early returns above.
  const weightRule = resolveSalesOrderSizeRule(activeDraft.status, activeDraft.submittedSnapshotJson);
  // 86d3c2wd1: frozen pricing inputs for the read-only Pricing tab so it renders the as-posted
  // numbers from the draft snapshot — no live FO needed (the FO re-fetch is skipped for
  // submitted SOs). gradeCode → markupPct snapshot + the frozen rebar VAT rate.
  const frozenMarkups = isReadOnly
    ? {
        '33': activeDraft.grade230Markup ?? 0,
        '40': activeDraft.grade275Markup ?? 0,
        '60': activeDraft.grade415Markup ?? 0,
        '75': activeDraft.grade520Markup ?? 0,
        '80': activeDraft.grade550Markup ?? 0,
      }
    : undefined;
  const frozenTaxRate = isReadOnly ? (activeDraft.rebarTaxRate ?? DEFAULT_REBAR_VAT) : undefined;
  const foPicked = Boolean(activeDraft.linkedFirmOfferInternalId);
  // FO detail still in flight — unified across the fresh manual pick (sets
  // pendingFoSummary, but linkedFirmOfferInternalId only lands after the fetch) and
  // the reload re-fetch (foPicked already true). Both set isLoadingFirmOffer (86d3a1q88).
  const foLoading = isLoadingFirmOffer && !selectedFirmOffer;
  // Render the workspace shell whenever an FO is linked OR being picked/loaded — only a
  // genuinely FO-less draft falls back to the "Pick a Firm Offer" empty state. During
  // load the FO-derived areas (pricing, per-grade chips, freight) show scoped skeletons.
  const hasFoContext = foPicked || Boolean(pendingFoSummary) || foLoading || Boolean(selectedFirmOffer);
  // Line-item extraction in flight for THIS draft (store-scoped by id so the badge
  // follows the right draft across a mid-extraction switch — 86d3a2agk).
  const isExtracting = extractingDraftId != null && extractingDraftId === activeDraft.id;
  const isArchived = activeDraft.isArchived;
  // 86d3d2hvz: archived drafts are terminal — no autosave, no Save/Create, body fully
  // disabled, only Restore/Delete. `isEditable` gates the edit actions; `readOnlyBody`
  // disables the Order Details cabinet + line-item tables (submitted/posting keep their
  // own read-only summary-band path via isReadOnly).
  const isEditable = !isReadOnly && !isArchived;
  const readOnlyBody = isReadOnly || isArchived;
  // 86d3mnuxe — archivable from Draft when not mid-post. A failed post is a Draft entity, so it's
  // archivable (abandon a post that won't succeed, e.g. FO fully drawn); an in-flight post is not.
  const archivable = !isArchived && activeDraft.status === SalesOrderStatus.Draft && !isPosting;

  return (
    <div className="flex h-full flex-col">
      {/* ── Header bar (mirrors QuotationDetailPanel:841-1044) ─────────────
          Compact px-4 py-2 padding. Status icon at left in a rounded square,
          identifier + customer/project + audit chips in the middle, action
          cluster on the right (AutoSave, Save, Submit, Archive/Delete). */}
      <div className="flex items-start justify-between gap-3 border-b bg-background px-4 py-2">
        <div className="flex min-w-0 items-start gap-3">
          <div
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-md',
              isSubmitted
                ? 'bg-success/10 text-success'
                : isPosting
                  ? 'bg-info/10 text-info'
                  : isPostingFailed
                    ? 'bg-destructive/10 text-destructive'
                    : 'bg-muted text-muted-foreground',
            )}
          >
            <FileCheck className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 truncate font-mono text-sm font-medium">
              <span className="truncate">{activeDraft.salesOrderNumber || 'New Sales Order'}</span>
              {activeDraft.id > 0 && <SalesOrderStatusBadge status={activeDraft.status} />}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {activeDraft.customerName || activeDraft.projectName || '(no customer)'}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
              {activeDraft.createdByName && (
                <span
                  className="inline-flex items-center gap-1 whitespace-nowrap"
                  title={`Created by ${activeDraft.createdByName}`}
                >
                  <UserRound className="h-3 w-3 shrink-0" />
                  <span className="truncate">Created by {activeDraft.createdByName}</span>
                </span>
              )}
              {activeDraft.linkedFirmOfferTranId && (
                <span
                  className="inline-flex items-center gap-1 whitespace-nowrap"
                  title={`Drawn from ${activeDraft.linkedFirmOfferTranId}`}
                >
                  <ClipboardList className="h-3 w-3 shrink-0" />
                  <span className="truncate font-mono">{activeDraft.linkedFirmOfferTranId}</span>
                </span>
              )}
              {(activeDraft.updatedAt || activeDraft.createdAt) && (
                <span
                  className="inline-flex items-center gap-1 whitespace-nowrap"
                  title={`Last edited ${formatDateTime12h(activeDraft.updatedAt ?? activeDraft.createdAt)}`}
                >
                  <Pencil className="h-3 w-3" />
                  {formatRelative(activeDraft.updatedAt ?? activeDraft.createdAt)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Review toggle — switches body to side-by-side source ↔ extracted
              lines view. Disabled when there's nothing recorded as a source. */}
          <Button
            type="button"
            size="sm"
            variant={reviewMode ? 'secondary' : 'outline'}
            onClick={() => setReviewMode((v) => !v)}
            title={
              reviewMode
                ? 'Back to compose'
                : 'Review source vs extracted lines'
            }
            aria-pressed={reviewMode}
            disabled={!activeDraft.extractionText && !activeDraft.extractionFileName}
          >
            {reviewMode ? (
              <X className="mr-1.5 h-3.5 w-3.5" />
            ) : (
              <Eye className="mr-1.5 h-3.5 w-3.5" />
            )}
            {reviewMode ? 'Back' : 'Review'}
          </Button>
          {isEditable && (
            <>
              <AutoSaveStatusChip
                isSaving={isSaving}
                lastSavedAt={lastSavedAt}
                saveError={saveError}
                onRetry={() => void handleManualSave()}
                disabled={isSubmitting}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleManualSave}
                disabled={isSaving || isSubmitting}
              >
                <Save className="mr-1.5 h-3.5 w-3.5" />
                Save
              </Button>
              {canSubmit && (
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSubmitClick}
                  // 86d3mnuxe funnel — one posting intent at a time: while a job is in flight or
                  // Failed, submit is disabled and the posting card's Retry/Cancel/Reconcile is the
                  // way forward (the BE claim rejects it too).
                  disabled={isSaving || isSubmitting || !foPicked || postingBlocksSubmit(posting)}
                  title={
                    !foPicked
                      ? 'Pick a Firm Offer first'
                      : submitBlockedReason(posting) ?? 'Create a Sales Order in NetSuite'
                  }
                >
                  {isSubmitting ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Send className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Create Sales Order
                </Button>
              )}
            </>
          )}
          {canArchive && archivable && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleArchive}
              disabled={isSaving || isSubmitting}
              title="Archive this draft (recoverable via the Archived tab)"
            >
              <Archive className="mr-1.5 h-3.5 w-3.5" />
              Archive
            </Button>
          )}
          {canArchive && isArchived && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleRestore}
              title="Restore this draft — returns it to Drafts, editable again"
            >
              <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />
              Restore
            </Button>
          )}
          {canDelete && isArchived && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleDelete}
              className="text-destructive hover:bg-destructive/10"
              title="Delete this draft"
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* ── Order Details ──────────────────────────────────────────────────
          86d3bujyr: editable drafts get the cabinet trigger (opens the Readiness
          Checklist drawer); a read-only/posted SO gets a flat static summary band
          inline instead (mirrors Quotation's ReadOnlySummaryBand). */}
      {!isReadOnly && (
      <button
          ref={cabinetTriggerRef}
          type="button"
          onClick={() => setCabinetOpen(!cabinetOpen)}
          aria-expanded={cabinetOpen}
          aria-controls="sales-order-cabinet-overlay"
          title={cabinetOpen ? 'Hide order details' : 'Show order details'}
          className={cn(
            'flex w-full shrink-0 items-center justify-between gap-2 border-b px-4 py-2 text-sm transition-colors',
            cabinetOpen
              ? 'bg-muted text-foreground'
              : 'bg-muted/30 text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <ClipboardList className="h-4 w-4 shrink-0" />
            <span className="shrink-0">Order Details</span>
            {/* While the FO re-fetches, surface a syncing hint here so the loading
                state is visible even with the cabinet collapsed (86d3abjqx — the
                FO-derived skeletons live inside the collapsed cabinet content). */}
            {foLoading ? (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                Syncing Firm Offer…
              </span>
            ) : (
              <span className="text-xs opacity-70">
                {cabinetOpen ? '— click to hide' : '— click to expand'}
              </span>
            )}
          </span>
          {cabinetOpen
            ? <ChevronUp className="h-4 w-4 shrink-0" />
            : <ChevronDown className="h-4 w-4 shrink-0" />}
        </button>
      )}

      {/* ── Eligibility / connectivity banner ──────────────────────────────
          Shown when the linked FO couldn't be (re)loaded. The store distinguishes
          a genuine 404 ("no longer eligible") from a transient NetSuite/network
          failure; Retry re-runs the fetch (the FO is usually fine — it was a blip).
          86d3bujyr: not shown in read-only — a posted SO can't act on FO eligibility. */}
      {eligibilityBanner && isEditable && (
        <div
          role="status"
          className="flex shrink-0 items-center gap-3 border-y border-warning/30 bg-warning/10 px-4 py-2.5 text-sm text-warning"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">{eligibilityBanner}</div>
          {activeDraft?.linkedFirmOfferInternalId && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="shrink-0"
              onClick={() => void loadLinkedFirmOffer()}
              disabled={isLoadingFirmOffer}
            >
              {isLoadingFirmOffer ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              )}
              Retry
            </Button>
          )}
        </div>
      )}

      {/* ── Body region ────────────────────────────────────────────────────
          relative for the cabinet overlay; flex-col + min-h-0 so the inner
          scroll container resolves a definite height. */}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {/* Read-only summary lives INSIDE the scroll region so it scrolls
              together with the line items, weight breakdown and freight total as
              one scrollable content (86d3ch831). */}
          {isReadOnly && <SalesOrderReadOnlySummary draft={activeDraft} />}
          {/* 86d3bujyr: Submitted posting details now live in the inline
              SalesOrderReadOnlySummary band's header (Quotation parity) — no
              separate posting card. Posting/PostingFailed keep their status card. */}
          {posting && (
            <div className="border-b p-4">
              <SalesOrderPostingStatusCard
                posting={posting}
                onRetry={() => void handleRetryFailedPost()}
                onReconcile={() => void handleReconcilePost()}
                onCancel={canResolvePosting ? () => void handleCancelPost() : null}
                isBusy={isSubmitting}
              />
            </div>
          )}

          {foPicked && selectedFirmOffer && reviewMode ? (
            // 86d3fcbte — review goes full-bleed: the shell portals to <body> and
            // covers the app sidebar + draft list. The split renders nothing in-
            // flow here; exiting flips reviewMode and the compose branch returns.
            <FullscreenReviewShell
              title={activeDraft.salesOrderNumber || 'New Sales Order'}
              subtitle={activeDraft.customerName || activeDraft.projectName || '(no customer)'}
              onExit={() => setReviewMode(false)}
            >
              <SalesOrderReviewLayout
                source={
                  <SalesOrderReviewSource
                    extractionText={activeDraft.extractionText}
                    extractionFileName={activeDraft.extractionFileName}
                    sessionFiles={sessionFiles}
                  />
                }
                lines={
                  <SalesOrderReviewLineItems
                    lineItems={activeDraft.lineItems}
                    readOnly={readOnlyBody}
                    onChange={(next) => updateDraftFields({ lineItems: next })}
                    onRematchRow={(id) => void rematchSingle(id)}
                    onRevertRow={handleRevertSingle}
                  />
                }
              />
            </FullscreenReviewShell>
          ) : hasFoContext ? (
            <div className="flex flex-col">
              {/* Line Items panel — mirrors quotation's QuotationLineItemsTable
                  card. Toolbar (title + actions) → bulk-action subheader →
                  body (table or empty-state intake) → footer (add / re-extract).
                  Flat layout (no outer border / radius) so the section is flush
                  with the panel edges — matches the quotation workspace's
                  edge-to-edge feel. */}
              {(() => {
                const lineCount = activeDraft.lineItems.length;
                const hasLines = lineCount > 0;
                // All rows always render; the tabs switch column-visibility,
                // not row-visibility. This matches quotation's behavior and
                // means the row counts / bulk actions stay consistent across
                // tabs.
                const visibleLines = activeDraft.lineItems;

                return (
                  // 86d3hdy52: wrap the line-items stack as one bg-card section with a
                  // top+bottom border — mirrors quotation's QuotationLineItemsTable Card
                  // (bg-card, border-y, edge-to-edge). Inner strips drop their own
                  // bg-background so they inherit this white; bg-muted/40 accent bands stay.
                  <div className="flex flex-col border-y bg-card">
                    {/* ── Per-grade FO balance dashboard ────────────
                        Pinned to the top of the line-items panel so sales sees
                        the per-grade draw-down budget before scanning / editing
                        the lines. */}
                    {/* Show the FO's per-grade authorized limits even before any lines
                        exist, so the coordinator sees the draw-down budget up front
                        (requested = 0 until lines are added) — 86d3abjqx. */}
                    {/* 86d3bujyr: the per-grade draw-down dashboard (incl. the embedded
                        "Exceeds Firm Offer balance" over-draw banner) is a draft-authoring
                        aid — hide it on a posted/read-only SO. */}
                    {sourceListedKg && !isReadOnly && (
                      <div className="border-b px-3 py-2">
                        <SourceKgNotice />
                      </div>
                    )}
                    {(selectedFirmOffer || foLoading) && !isReadOnly && (
                      <div className="border-b px-3 py-3">
                        <PerGradeBalanceChips
                          firmOffer={selectedFirmOffer}
                          lineItems={activeDraft.lineItems}
                          isLoading={foLoading}
                        />
                      </div>
                    )}
                    {/* ── Toolbar ───────────────────────────────────── */}
                    <div
                      id="so-line-items"
                      className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2"
                    >
                      <div className="flex items-center gap-2 text-sm">
                        <ClipboardList className="h-4 w-4 text-muted-foreground" />
                        <span className="font-semibold">Line Items</span>
                        {lineCount > 0 && (
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {lineCount}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1">
                        {isEditable && (
                          <SpecAdvisoryBadges
                            advisories={specAdvisories}
                            onApply={applyAdvisory}
                            className="mr-1"
                          />
                        )}
                        {isEditable && (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={handleAddLine}
                              className="h-8 gap-1.5 text-xs"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Add Line
                            </Button>
                            {/* Re-extraction lives at the bottom now; the toolbar shows only the
                                in-flight "Extracting…" badge (86d3a2agk), mirroring quotation. */}
                            {isExtracting && (
                              <span className="inline-flex h-8 items-center gap-1.5 rounded-md border border-info/30 bg-info/10 px-2.5 text-xs font-medium text-info">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Extracting…
                              </span>
                            )}
                          </>
                        )}
                        <Tabs
                          value={lineFilter}
                          onValueChange={(v) => setLineFilter(v as LineItemsViewMode)}
                        >
                          <TabsList className="h-8">
                            <TabsTrigger value="all" className="h-6 px-2 text-xs">
                              All
                            </TabsTrigger>
                            <TabsTrigger value="spec" className="h-6 px-2 text-xs">
                              Spec
                            </TabsTrigger>
                            <TabsTrigger value="pricing" className="h-6 px-2 text-xs">
                              Pricing
                            </TabsTrigger>
                          </TabsList>
                        </Tabs>
                        {/* View Source removed (86d3f95ca) — it duplicated the header
                            Review button (both enter reviewMode). Review is canonical. */}
                      </div>
                    </div>

                    {/* ── Quick Select bar ──────────────────────────
                        Toggleable per-bucket buttons that fan out into the
                        row-selection state. Visibility per button is persisted
                        per user in salesOrderPreferencesStore. */}
                    {isEditable && (
                      <QuickSelectBar
                        quickSelect={quickSelect}
                        rowSelection={rowSelection}
                        visible={visibleQuickSelects}
                        onSetVisible={setQuickSelectVisible}
                        onToggleGroup={toggleGroupSelection}
                      />
                    )}

                    {/* ── Bulk-action subheader ─────────────────────────
                        Mirrors QuotationLineItemsTable's bulk toolbar. 86d3bujyr: hidden
                        entirely in read-only (a submitted SO has nothing to bulk-action) —
                        matches QuotationLineItemsTable instead of showing dead buttons. */}
                    {isEditable && (
                    <div className="flex flex-wrap items-center gap-2 border-b bg-muted/40 px-3 py-2 text-sm">
                      <span
                        className={cn(
                          'font-medium',
                          selectedCount === 0 && 'text-muted-foreground',
                        )}
                      >
                        {selectedCount > 0
                          ? `${selectedCount} selected`
                          : 'No rows selected'}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={selectedCount === 0 || isReadOnly}
                        onClick={() => setBulkEditOpen(true)}
                      >
                        Bulk Edit…
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={selectedCount === 0 || isReadOnly}
                        onClick={() => void handleBulkRematch()}
                      >
                        <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                        Re-match
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={selectedCount === 0 || isReadOnly}
                        onClick={handleBulkRevert}
                      >
                        <Undo2 className="mr-1.5 h-3.5 w-3.5" />
                        Revert
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-destructive hover:text-destructive"
                        disabled={selectedCount === 0 || isReadOnly}
                        onClick={handleBulkDelete}
                      >
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                        Delete
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground"
                        disabled={selectedCount === 0}
                        onClick={handleClearSelection}
                      >
                        <X className="mr-1 h-3.5 w-3.5" />
                        Clear
                      </Button>
                    </div>
                    )}

                    {/* ── Body ─────────────────────────────────────── */}
                    <div className="px-3 py-3">
                      {hasLines ? (
                        <SalesOrderLineItemsTable
                          lineItems={visibleLines}
                          firmOffer={selectedFirmOffer}
                          frozenMarkups={frozenMarkups}
                          frozenTaxRate={frozenTaxRate}
                          isLoading={foLoading}
                          viewMode={lineFilter}
                          rowSelection={rowSelection}
                          onRowSelectionChange={setRowSelection}
                          onRematchRow={(id) => void rematchSingle(id)}
                          onRevertRow={handleRevertSingle}
                          onApplyRowAdvisory={applyRowAdvisory}
                          onChange={(next) => updateDraftFields({ lineItems: next })}
                          readOnly={readOnlyBody}
                        />
                      ) : (
                        <div className="space-y-3">
                          <div className="py-1">
                            <h3 className="text-sm font-semibold">No line items yet</h3>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Paste text or upload one or more files and the AI will extract
                              rebar line items. You can also use{' '}
                              <span className="font-medium">+ Add Line</span> below to add
                              one manually.
                            </p>
                          </div>
                          {isEditable && (
                            <SalesOrderIntakePanel
                              onExtract={handleStartExtraction}
                              onBeforeExtract={confirmReplaceIfNeeded}
                              isExtracting={isExtracting}
                              compact
                            />
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Footer (line-item actions duplicated here too) ─
                        One bar: Add Line / Re-extract on the left (editable only), and a
                        mirror of the top All/Spec/Pricing tabs on the right — bound to the
                        same lineFilter so they stay in sync, lets the user switch views
                        after scrolling a long list (86d3f95ca). The tabs are hasLines-gated
                        (independent of isEditable) so they also show on read-only/submitted
                        SOs, exactly like the top tabs. Merged into one row to avoid stacking
                        two bordered bars. */}
                    {(isEditable || hasLines) && (
                      <div className="flex items-center justify-between gap-1.5 border-t px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          {isEditable && (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={handleAddLine}
                                className="h-8 gap-1.5 text-xs"
                              >
                                <Plus className="h-3.5 w-3.5" />
                                Add Line
                              </Button>
                              {/* Re-extract only once lines exist — first extraction happens in
                                  the inline intake panel below (86d3a2agk). */}
                              {hasLines && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setExtractDialogOpen(true)}
                                  disabled={isExtracting}
                                  className="h-8 gap-1.5 text-xs"
                                >
                                  <Sparkles className="h-3.5 w-3.5" />
                                  Re-extract from source
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                        {hasLines && (
                          <Tabs
                            value={lineFilter}
                            onValueChange={(v) => setLineFilter(v as LineItemsViewMode)}
                          >
                            <TabsList className="h-8">
                              <TabsTrigger value="all" className="h-6 px-2 text-xs">
                                All
                              </TabsTrigger>
                              <TabsTrigger value="spec" className="h-6 px-2 text-xs">
                                Spec
                              </TabsTrigger>
                              <TabsTrigger value="pricing" className="h-6 px-2 text-xs">
                                Pricing
                              </TabsTrigger>
                            </TabsList>
                          </Tabs>
                        )}
                      </div>
                    )}

                    {/* ── Weight Breakdown (ticket 86d3ch80u) ───────────
                        Mirrors quotation's band: delivered weight split by
                        diameter, below the table and above the freight total. */}
                    {hasLines && (
                      <SalesOrderWeightBreakdown lineItems={activeDraft.lineItems} rule={weightRule} />
                    )}

                    {/* ── Freight & Order Total (ticket 86d39jbhk) ───────
                        Mirrors quotation's Weight Breakdown placement: a
                        bg-muted/40 band below the table/actions. Shows the
                        freight computation + combined order total. Hidden when
                        there's no delivered weight. */}
                    {hasLines && (
                      <SalesOrderFreightBlock
                        lineItems={activeDraft.lineItems}
                        firmOffer={selectedFirmOffer}
                        // 86d3fteqa: effective editable Freight/Ton rate drives the charge;
                        // defaults from the FO rate (frozen submitted value for read-only SOs).
                        freightPerTon={
                          activeDraft.freightChargePerTon ??
                          selectedFirmOffer?.freightChargePerTon ??
                          null
                        }
                        waivedFreight={activeDraft.waivedFreight}
                        isDiscountedFreight={activeDraft.isDiscountedFreight}
                        separateBilling={activeDraft.separateBilling}
                        includeFreightLine={activeDraft.includeFreightLine}
                        isLoading={foLoading}
                        frozenMarkups={frozenMarkups}
                        frozenTaxRate={frozenTaxRate}
                      />
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center p-8">
              <div className="max-w-sm text-center">
                <ClipboardList className="mx-auto h-10 w-10 text-muted-foreground/70" />
                <h2 className="mt-3 text-base font-semibold">Pick a Firm Offer to start</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Choose the Firm Offer this sales order draws from — customer, pricing, and
                  grade balances carry over automatically.
                </p>
                {isEditable && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => setFoModalOpen(true)}
                  >
                    Select Firm Offer
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Cabinet overlay — single drawer holding the Readiness Checklist.
            Always mounted (visibility toggled via opacity / pointer-events) so
            internal state survives open/close cycles. Rendered in read-only too,
            where the checklist becomes the submitted summary-of-record. */}
        <div
            id="sales-order-cabinet-overlay"
            ref={cabinetRef}
            className={cn(
              'absolute inset-x-0 top-0 z-40 h-[80%] max-h-[720px] overflow-y-auto border-b bg-background shadow-xl transition-opacity',
              cabinetOpen ? 'opacity-100' : 'pointer-events-none invisible opacity-0',
            )}
            aria-hidden={!cabinetOpen}
          >
            <div className="p-4">
              {/* § Firm Offer ─ the creation seed. Read-only chip + Select/Change;
                  the searchable list lives in the FoPickerModal. */}
              <SectionHeader icon={ClipboardList} title="Firm Offer" />
              <div className="mt-2 flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                <div className="min-w-0">
                  {activeDraft.linkedFirmOfferTranId ? (
                    <span className="truncate font-mono text-sm font-semibold">
                      {activeDraft.linkedFirmOfferTranId}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">No Firm Offer selected</span>
                  )}
                </div>
                {isEditable && (
                  <Button variant="outline" size="sm" onClick={() => setFoModalOpen(true)}>
                    {activeDraft.linkedFirmOfferTranId ? 'Change FO' : 'Select Firm Offer'}
                  </Button>
                )}
              </div>

              <div className="mt-2">
                <FoDetailCard />
              </div>

              <FoPickerModal
                open={foModalOpen}
                onOpenChange={setFoModalOpen}
                onPick={(fo) => {
                  setFoModalOpen(false);
                  void handleFoPick(fo);
                }}
              />

              {/* § Order Details ─ Readiness Checklist. The status-badged
                  accordion supersedes the old flat FO-summary + compose-form
                  sections; statuses derive from the one sectionStatus model.
                  86d3bujyr: NOT rendered in read-only — the inline
                  SalesOrderReadOnlySummary band replaces the editable form there. */}
              {hasFoContext && !isReadOnly && (
                <div className="mt-6">
                  {/* FormProvider scopes the RHF instance to the Order Details bodies so
                      their text inputs register uncontrolled (86d3afyp6). The remount key
                      stays for now — the not-yet-migrated address picker relies on it. */}
                  <FormProvider {...formMethods}>
                    <SalesOrderReadinessChecklist
                      ref={checklistRef}
                      key={activeDraft.id || 'new'}
                      draft={activeDraft}
                      firmOffer={selectedFirmOffer}
                      isLoadingFirmOffer={foLoading}
                      readOnly={readOnlyBody}
                      keyboardActive={cabinetOpen}
                      sections={sections}
                    />
                  </FormProvider>
                </div>
              )}
            </div>
          </div>
      </div>

      {/* ── Sticky readiness bar ───────────────────────────────────────────
          Pinned at the foot of the panel while drafting. Lists every blocker
          (form sections + read-only line-items reference) with jump links;
          submit is gated by these same blockers via the pre-submit review. */}
      {foPicked && isEditable && (
        <ReadinessBar
          sections={sections}
          lineItems={lineItemsReadiness}
          onJumpSection={jumpToSection}
          onJumpLineItems={jumpToLineItems}
        />
      )}

      {/* Bulk edit dialog — applies a partial patch to every selected row. */}
      <SalesOrderBulkEditDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        selectedCount={selectedCount}
        onApply={handleBulkEditApply}
      />

      {/* Re-extract dialog — opens the same intake (paste/upload) surface
          inside a modal when there are already line items, so users can
          extract more without losing their place on the table. */}
      <Dialog open={extractDialogOpen} onOpenChange={setExtractDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Extract line items</DialogTitle>
            <DialogDescription>
              Paste an order list or upload one or more files. The AI extracts rebar
              line items and replaces the current draft's line items.
            </DialogDescription>
          </DialogHeader>
          <SalesOrderIntakePanel
            onExtract={(source) => {
              // Fire-and-close: dismiss the modal immediately, run extraction in the
              // background; the toolbar "Extracting…" badge tracks it (86d3a2agk).
              setExtractDialogOpen(false);
              handleStartExtraction(source);
            }}
            onBeforeExtract={confirmReplaceIfNeeded}
            isExtracting={isExtracting}
            compact
          />
        </DialogContent>
      </Dialog>

      {/* Pre-submit review — final read-only checkpoint before the irreversible
          NetSuite POST. Only reachable once every blocker is cleared. */}
      <PreSubmitReviewDialog
        open={preSubmitOpen}
        onOpenChange={setPreSubmitOpen}
        draft={activeDraft}
        firmOffer={selectedFirmOffer}
        lineItems={lineItemsReadiness}
        onConfirm={() => void confirmSubmit()}
        isSubmitting={isSubmitting}
      />

      {/* Pricing drift — 409 PRICING_DRIFT: FO markup changed since save. Reuses
          the quotation drift dialog; Refresh re-stamps markup + resubmits. */}
      {pricingDrift && (
        <PricingDriftDialog
          detail={pricingDrift}
          open={!!pricingDrift}
          onClose={clearPricingDrift}
          onRefreshAndResubmit={handleRefreshAndResubmit}
          // Show the matched SKU description per drifted line, not the raw NetSuite item id.
          // (No onKeepQuoted — SO is refresh-only: the FO is a customer contract.)
          resolveItemLabel={(id) =>
            activeDraft.lineItems.find((li) => li.matchedItemId === id)?.matchedSalesDescription ?? undefined
          }
        />
      )}

    </div>
  );
}

// ── Review view ───────────────────────────────────────────────────────────
// Side-by-side panel that pairs the extraction source (text or file name)
// with a compact list of matched lines so sales can sanity-check that the
// AI didn't miss / mis-match anything before submitting. Mirrors the role
// of QuotationReviewView in the quotation workspace.

// Generic split-pane: source on the left, line items on the right, with a
// draggable divider on xl+ screens (stacks vertically below xl). Mirrors
// QuotationReviewView's layout.
function SalesOrderReviewLayout({
  source,
  lines,
}: {
  source: React.ReactNode;
  lines: React.ReactNode;
}) {
  // Source can shrink to 15% so the line-items pane can be dragged up to 85% to
  // read the table — 86d3fcbte. Kept in sync with the Quotation review split.
  const MIN_RATIO = 0.15;
  const MAX_RATIO = 0.85;
  const [ratio, setRatio] = useState(0.58);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const clamped = Math.max(MIN_RATIO, Math.min(MAX_RATIO, ratio));

  function startDrag(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const handle = e.currentTarget;

    // 86d3fcbte — pointer CAPTURE on the handle, not document mouse listeners.
    // The source pane embeds the PDF in an <object> (its own browsing context);
    // a 'mouseup' fired over that object never reaches the document, so the drag
    // never ended and the divider kept following the cursor after release.
    // setPointerCapture redirects every pointermove/up to the handle regardless
    // of what sits underneath, and pointercancel/up reliably end the drag.
    handle.setPointerCapture(e.pointerId);

    const onMove = (ev: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      if (rect.width === 0) return;
      setRatio((ev.clientX - rect.left) / rect.width);
    };
    const stop = (ev: PointerEvent) => {
      handle.releasePointerCapture?.(ev.pointerId);
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', stop);
      handle.removeEventListener('pointercancel', stop);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', stop);
    handle.addEventListener('pointercancel', stop);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  }

  return (
    <div
      ref={containerRef}
      className="flex h-full min-h-0 flex-col xl:flex-row"
      style={{ '--source-col': `${(clamped * 100).toFixed(2)}%` } as React.CSSProperties}
    >
      <div className="h-[55vh] w-full min-w-0 shrink-0 grow-0 border-b xl:h-auto xl:basis-[var(--source-col)] xl:border-b-0 xl:border-r">
        {source}
      </div>
      <div
        role="separator"
        aria-orientation="vertical"
        onPointerDown={startDrag}
        title="Drag to resize"
        className="hidden w-1 shrink-0 cursor-col-resize touch-none bg-border transition-colors hover:bg-primary/40 active:bg-primary xl:block"
      />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto">{lines}</div>
    </div>
  );
}

// Left pane: dispatches the extraction source to the right viewer. In-session
// uploaded files get the rich zoom-able viewers (reused from the Quotation
// module); pasted text gets the text viewer; a reloaded file (bytes gone) shows
// the re-extract notice.
function SalesOrderReviewSource({
  extractionText,
  extractionFileName,
  sessionFiles,
}: {
  extractionText: string | null;
  extractionFileName: string | null;
  sessionFiles: File[];
}) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b bg-muted/30 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <span>Extraction source</span>
        {extractionFileName && (
          <span className="ml-2 max-w-[55%] truncate font-mono text-[10px] normal-case text-foreground">
            {extractionFileName}
          </span>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        {sessionFiles.length > 0 ? (
          <SessionFilePreview files={sessionFiles} />
        ) : extractionText ? (
          <div className="h-full min-h-0 overflow-auto">
            <ReviewTextViewer text={extractionText} />
          </div>
        ) : extractionFileName ? (
          <ReviewSourceNotice>
            This file is only reviewable during the session it was extracted (
            {extractionFileName}). After submitting or reloading the page, re-extract
            the file to review its contents again.
          </ReviewSourceNotice>
        ) : (
          <ReviewSourceNotice>
            No extraction source recorded — these line items were added manually.
          </ReviewSourceNotice>
        )}
      </div>
    </div>
  );
}

function ReviewSourceNotice({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
      <span>{children}</span>
    </div>
  );
}

// ── In-session file preview ───────────────────────────────────────────────
// Previews the uploaded file(s) for the current session only — the bytes are
// never persisted (the DB keeps just the filename). Uses the Quotation module's
// rich viewers: images zoom/pan, PDFs render natively, spreadsheets show a grid.
// A footer makes the session-only, re-extract-after-reload nature explicit.
function SessionFilePreview({ files }: { files: File[] }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = files[Math.min(activeIdx, files.length - 1)];
  const url = useObjectUrl(active);
  const kind = guessPreviewKind(active?.name ?? '');

  return (
    <div className="flex h-full min-h-0 flex-col">
      {files.length > 1 && (
        <div className="flex flex-wrap gap-1 border-b p-2">
          {files.map((f, i) => (
            <button
              key={`${f.name}-${i}`}
              type="button"
              onClick={() => setActiveIdx(i)}
              title={f.name}
              className={cn(
                'max-w-[12rem] truncate rounded px-2 py-1 font-mono text-[10px]',
                i === activeIdx
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70',
              )}
            >
              {f.name}
            </button>
          ))}
        </div>
      )}
      <div className="relative min-h-0 flex-1 bg-muted/10">
        {url && kind === 'image' ? (
          <ReviewImageViewer src={url} alt={active?.name ?? 'attachment'} />
        ) : url && kind === 'pdf' ? (
          <ReviewPdfViewer src={url} fileName={active?.name} />
        ) : kind === 'spreadsheet' && active ? (
          <ReviewSpreadsheetGridViewer
            file={active}
            fileName={active.name}
            fallback={
              <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
                {active.name} could not be parsed — open the original file for spec checks.
              </div>
            }
          />
        ) : (
          <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
            {active?.name} can't be previewed inline — open the original file for spec checks.
          </div>
        )}
      </div>
      <div className="border-t bg-muted/30 px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        Session-only preview — not saved. Re-extract after reload to view again.
      </div>
    </div>
  );
}

// Create an object URL for a File and revoke it on change/unmount.
function useObjectUrl(file: File | undefined): string | undefined {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!file) {
      setUrl(undefined);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return url;
}

// ── Sectioned cabinet header ──────────────────────────────────────────────
// Small all-caps label with an icon and a thin underline. Used to break the
// single Order Details cabinet into scannable Firm Offer / Inherited /
// Shipping & Delivery zones.

interface SectionHeaderProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  className?: string;
}

function SectionHeader({ icon: Icon, title, className }: SectionHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 border-b pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground',
        className,
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{title}</span>
    </div>
  );
}

// ── Quick Select bar ──────────────────────────────────────────────────────
// Renders the per-bucket "select all small Ø / no length / no match / etc."
// buttons above the bulk-action subheader. Visibility per button is driven
// by the SO preferences store. Each button toggles the entire group's
// selection on/off and shows a live count.

interface QuickSelectBarProps {
  quickSelect: Record<string, string[]>;
  rowSelection: import('@tanstack/react-table').RowSelectionState;
  visible: Record<string, boolean>;
  onSetVisible: (key: import('../../store/salesOrderPreferencesStore').QuickSelectKey, v: boolean) => void;
  onToggleGroup: (ids: string[]) => void;
}

function QuickSelectBar({
  quickSelect,
  rowSelection,
  visible,
  onSetVisible,
  onToggleGroup,
}: QuickSelectBarProps) {
  const totalLines = new Set<string>([
    ...quickSelect.smallDiameter,
    ...quickSelect.bigDiameter,
    ...quickSelect.noLength,
    ...quickSelect.noGrade,
    ...quickSelect.noQuantity,
    ...quickSelect.noMatch,
    ...quickSelect.weldable,
    ...quickSelect.notWeldable,
  ]).size;
  if (totalLines === 0) return null;

  const allSelected = (ids: string[]) =>
    ids.length > 0 && ids.every((id) => rowSelection[id]);

  return (
    <div className="flex flex-wrap items-center gap-2 border-b bg-muted/20 px-3 py-2 text-xs">
      <span className="font-medium text-muted-foreground">Quick select:</span>
      {visible.smallDiameter && (
        <QuickSelectChip
          label={QUICK_SELECT_LABELS.smallDiameter}
          count={quickSelect.smallDiameter.length}
          active={allSelected(quickSelect.smallDiameter)}
          onClick={() => onToggleGroup(quickSelect.smallDiameter)}
        />
      )}
      {visible.bigDiameter && (
        <QuickSelectChip
          label={QUICK_SELECT_LABELS.bigDiameter}
          count={quickSelect.bigDiameter.length}
          active={allSelected(quickSelect.bigDiameter)}
          onClick={() => onToggleGroup(quickSelect.bigDiameter)}
        />
      )}
      {visible.noLength && (
        <QuickSelectChip
          label="No length"
          count={quickSelect.noLength.length}
          active={allSelected(quickSelect.noLength)}
          onClick={() => onToggleGroup(quickSelect.noLength)}
        />
      )}
      {visible.noGrade && (
        <QuickSelectChip
          label="No grade"
          count={quickSelect.noGrade.length}
          active={allSelected(quickSelect.noGrade)}
          onClick={() => onToggleGroup(quickSelect.noGrade)}
        />
      )}
      {visible.noQuantity && (
        <QuickSelectChip
          label="No qty"
          count={quickSelect.noQuantity.length}
          active={allSelected(quickSelect.noQuantity)}
          onClick={() => onToggleGroup(quickSelect.noQuantity)}
        />
      )}
      {visible.noMatch && (
        <QuickSelectChip
          label="No match"
          count={quickSelect.noMatch.length}
          active={allSelected(quickSelect.noMatch)}
          onClick={() => onToggleGroup(quickSelect.noMatch)}
        />
      )}
      {visible.weldable && (
        <QuickSelectChip
          label="Weldable"
          count={quickSelect.weldable.length}
          active={allSelected(quickSelect.weldable)}
          onClick={() => onToggleGroup(quickSelect.weldable)}
        />
      )}
      {visible.notWeldable && (
        <QuickSelectChip
          label="Not weldable"
          count={quickSelect.notWeldable.length}
          active={allSelected(quickSelect.notWeldable)}
          onClick={() => onToggleGroup(quickSelect.notWeldable)}
        />
      )}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="ml-auto h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            aria-label="Configure visible quick selects"
            title="Configure visible quick selects"
          >
            <Settings2 className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-60" align="end">
          <div className="space-y-3">
            <div>
              <div className="text-sm font-semibold">Visible quick selects</div>
              <p className="text-[11px] text-muted-foreground">
                Choice persists in this browser.
              </p>
            </div>
            <div className="space-y-2">
              {QUICK_SELECT_KEYS.map((key) => (
                <label
                  key={key}
                  className="flex cursor-pointer items-center gap-2 text-sm"
                >
                  <Checkbox
                    checked={visible[key]}
                    onCheckedChange={(v) => onSetVisible(key, v === true)}
                  />
                  <span>{QUICK_SELECT_LABELS[key]}</span>
                </label>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

interface QuickSelectChipProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}

// Matches QuotationLineItemsTable's quick-select button styling: shadcn
// Button with outline variant, switching to blue-600 + white when the
// whole group is selected. Label and count inline with a middle dot.
function QuickSelectChip({ label, count, active, onClick }: QuickSelectChipProps) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'default' : 'outline'}
      disabled={count === 0}
      onClick={onClick}
      className={cn(
        'h-7 px-2 text-xs',
        active && 'bg-info text-info-foreground hover:bg-info/90',
      )}
      title={
        count === 0
          ? `No ${label.toLowerCase()} rows`
          : active
            ? 'Click to unselect this group'
            : 'Click to add this group to the selection'
      }
    >
      {label} · {count}
    </Button>
  );
}

